import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CoreApiError, getApplicationTenants, refreshAuthToken, selectSite } from '../services/coreApi';
import { authStorage, decodeTokenClaims } from '../services/authStorage';
import { knownTenants, rememberTenants } from '../services/tenantCatalog';
import { tokenForTenant } from '../services/tenantSwitch';
import { useAuth } from '../hooks';
import type { AuthTokenResponse, CoreTenant } from '../types';
import { resolveActiveRole, rolesFromClaims } from '../utils/userDisplay';
import { openRoleHome, SESSION_HOME_EVENT } from '../utils/sessionNavigation';

interface TenantContextValue {
  /** Tenants Core returned for the current token. Directory filters use this list. */
  tenants: CoreTenant[];
  /** Every organisation this person has been able to see, including other roles. */
  switchableTenants: CoreTenant[];
  currentTenantId: string | null;
  currentTenant: CoreTenant | null;
  currentSiteId: string | null;
  loading: boolean;
  switching: boolean;
  switchTenant: (tenantId: string) => Promise<void>;
  switchSite: (tenantSiteId: string) => Promise<void>;
  error: string | null;
}

const TenantContext = createContext<TenantContextValue | null>(null);

function accountAfterSwitch(data: AuthTokenResponse, fallbackRefreshToken?: string) {
  const nextAccount = resolveActiveRole(
    rolesFromClaims(decodeTokenClaims(data.accessToken)),
    authStorage.getPreferredRole(),
  );
  if (!nextAccount) {
    throw new Error('No Passport role on switched token');
  }
  authStorage.setSession({
    accessToken: data.accessToken,
    expiresIn: data.expiresIn,
    refreshToken: data.refreshToken ?? fallbackRefreshToken,
    user: data.user ?? authStorage.getCachedUser()!,
  });
  return nextAccount;
}

function subjectId(): string | null {
  const subject = authStorage.getTokenClaims().sub;
  return typeof subject === 'string' && subject ? subject : null;
}

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [tenants, setTenants] = useState<CoreTenant[]>([]);
  const [switchableTenants, setSwitchableTenants] = useState<CoreTenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimVersion, setClaimVersion] = useState(0);

  useEffect(() => {
    const syncClaims = () => setClaimVersion(version => version + 1);
    window.addEventListener(SESSION_HOME_EVENT, syncClaims);
    return () => window.removeEventListener(SESSION_HOME_EVENT, syncClaims);
  }, []);

  const currentTenantId = useMemo(() => {
    const claims = authStorage.getTokenClaims();
    return typeof claims.tenantId === 'string' ? claims.tenantId : null;
  }, [isAuthenticated, tenants, claimVersion]);

  const currentSiteId = useMemo(() => {
    const claims = authStorage.getTokenClaims();
    return typeof claims.tenantSiteId === 'string' ? claims.tenantSiteId : null;
  }, [isAuthenticated, tenants, claimVersion]);

  useEffect(() => {
    if (!isAuthenticated) {
      setTenants([]);
      setSwitchableTenants([]);
      return;
    }
    const userId = subjectId();
    setSwitchableTenants(knownTenants(userId));
    let cancelled = false;
    setLoading(true);
    getApplicationTenants()
      .then(list => {
        if (cancelled) return;
        setTenants(list);
        setSwitchableTenants(rememberTenants(userId, list));
      })
      .catch(() => {
        if (cancelled) return;
        const known = knownTenants(userId);
        if (known.length === 0) setError('Could not load tenants');
        setSwitchableTenants(known);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, claimVersion]);

  const openSwitchedSession = useCallback((data: AuthTokenResponse, fallbackRefreshToken?: string) => {
    openRoleHome(accountAfterSwitch(data, fallbackRefreshToken));
    setSwitching(false);
  }, []);

  const switchWithFallback = useCallback(async (
    run: (accessToken?: string) => Promise<AuthTokenResponse>,
    failureMessage: string,
  ) => {
    setSwitching(true);
    setError(null);
    const generation = authStorage.currentGeneration();
    const sessionMoved = () => authStorage.currentGeneration() !== generation;
    try {
      try {
        openSwitchedSession(await run());
      } catch (error) {
        // The select already stored a new session. Refreshing the previous
        // token would put the old organisation back.
        if (sessionMoved()) {
          const role = authStorage.getActiveRole();
          if (role) openRoleHome(role);
          setSwitching(false);
          return;
        }
        const refreshToken = authStorage.getRefreshToken();
        if (!refreshToken) throw error;
        const refreshed = await refreshAuthToken(refreshToken);
        if (sessionMoved() || authStorage.getRefreshToken() !== refreshToken) {
          const role = authStorage.getActiveRole();
          if (role) openRoleHome(role);
          setSwitching(false);
          return;
        }
        authStorage.updateTokens(refreshed.accessToken, refreshed.expiresIn, refreshed.refreshToken);
        openSwitchedSession(await run(refreshed.accessToken), refreshed.refreshToken);
      }
    } catch (error) {
      const message = error instanceof CoreApiError && error.message
        ? error.message
        : failureMessage;
      setError(message);
      setSwitching(false);
    }
  }, [openSwitchedSession]);

  const switchTenant = useCallback((tenantId: string) => {
    if (tenantId === currentTenantId) return Promise.resolve();
    return switchWithFallback(
      accessToken => tokenForTenant(
        tenantId,
        switchableTenants,
        accessToken,
        authStorage.getPreferredRole(),
      ),
      'Could not switch tenant',
    );
  }, [currentTenantId, switchWithFallback, switchableTenants]);

  const switchSite = useCallback((tenantSiteId: string) => {
    if (tenantSiteId === currentSiteId) return Promise.resolve();
    return switchWithFallback(
      accessToken => selectSite(tenantSiteId, accessToken),
      'Could not switch site',
    );
  }, [currentSiteId, switchWithFallback]);

  const currentTenant = useMemo(() => {
    const fromSession = tenants.find(tenant => tenant.id === currentTenantId);
    if (fromSession) return fromSession;
    return switchableTenants.find(tenant => tenant.id === currentTenantId) ?? null;
  }, [tenants, switchableTenants, currentTenantId]);

  const value = useMemo<TenantContextValue>(
    () => ({
      tenants,
      switchableTenants,
      currentTenantId,
      currentTenant,
      currentSiteId,
      loading,
      switching,
      switchTenant,
      switchSite,
      error,
    }),
    [
      tenants,
      switchableTenants,
      currentTenantId,
      currentTenant,
      currentSiteId,
      loading,
      switching,
      switchTenant,
      switchSite,
      error,
    ],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTenants = (): TenantContextValue => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenants must be used within TenantProvider');
  return ctx;
};
